import React, { useState, useEffect } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from '../ui/sidebar';
import { Home, PoundSterling, BarChart3, Settings, ScanLine, HelpCircle, Play, Tags, Plug } from 'lucide-react';
import { ThemeToggle } from '../ui/theme-toggle';
import { Button } from '../ui/button';
import { RecapIcon } from '../ui/recap-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '../ui/dialog';
import { toast } from 'sonner';

const menuItems = [
  {
    title: 'Home',
    icon: Home,
    url: '#home',
  },
  {
    title: 'Receipts',
    icon: PoundSterling,
    url: '#receipts',
  },
  {
    title: 'Reports',
    icon: BarChart3,
    url: '#reports',
  },
];

export function AppSidebar() {
  const [currentHash, setCurrentHash] = useState(window.location.hash || '#home');
  const { setOpenMobile, isMobile } = useSidebar();
  const [tourStep, setTourStep] = useState(0);
  const [isTourActive, setIsTourActive] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const toastIdRef = React.useRef<string | number | null>(null);
  const isShowingToastRef = React.useRef(false);
  const shouldStopTourRef = React.useRef(false);
  const isNavigatingRef = React.useRef(false);

  const tourSteps = [
    { 
      hash: '#home', 
      title: '👋 Welcome to Recap!', 
      description: 'Recap helps you manage expense receipts easily. This is your home dashboard where you\'ll see your recent activity and quick stats.' 
    },
    { 
      hash: '#receipts', 
      title: '📱 Receipts Page', 
      description: 'This is where all your receipts live. You can upload, view, and organize them here. Let\'s learn how to add your first receipt!',
      target: '[data-tour="receipts-page"]'
    },
    { 
      hash: '#receipts', 
      title: '📸 Upload Your First Receipt', 
      description: 'Look for the "Upload Receipt" button (usually at the top right). Click it to upload a photo of your receipt from your phone or computer. You can take a photo or choose an existing image.',
      target: '[data-tour="upload-button"]'
    },
    { 
      hash: '#receipts', 
      title: '✅ Selecting Receipts', 
      description: 'After uploading, you\'ll see receipt cards. Simply click on any card to select it - you\'ll see a blue ring appear around selected receipts. You can select multiple receipts at once!',
      target: '[data-tour="receipt-grid"]'
    },
    { 
      hash: '#receipts', 
      title: '🤖 AI Processing', 
      description: 'Once you\'ve selected receipts, click the "Process Selected" button. Our AI will automatically extract merchant names, amounts, dates, and categories from your receipts - no manual typing needed!',
      target: '[data-tour="process-button"]'
    },
    { 
      hash: '#reports', 
      title: '📊 View Reports', 
      description: 'See your spending patterns over time. Analyze expenses by category, view trends, and export data for your records.' 
    },
    { 
      hash: '#receipt-types', 
      title: '⚙️ Manage Receipt Types', 
      description: 'Customize your receipt categories here. Add, edit, or remove receipt types to match your organization\'s needs.' 
    },
    { 
      hash: '#integrations', 
      title: '🔗 Configure Integrations', 
      description: 'Set up integrations with your expense management system and other products to streamline your workflow.' 
    },
  ];

  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash || '#home');
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (isTourActive && tourStep < tourSteps.length && !isShowingToastRef.current) {
      isShowingToastRef.current = true;
      const currentStep = tourSteps[tourStep];
      
      // Remove previous highlights
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
        el.style.position = '';
        el.style.zIndex = '';
      });
      
      // Dismiss all existing toasts first
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      
      // Navigate to the step
      window.location.hash = currentStep.hash;
      
      // Highlight the target element if specified
      if (currentStep.target) {
        setTimeout(() => {
          const targetElement = document.querySelector(currentStep.target);
          if (targetElement) {
            // Add highlight class
            targetElement.classList.add('tour-highlight');
            
            // Scroll into view smoothly
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add pulsing animation
            targetElement.style.position = 'relative';
            targetElement.style.zIndex = '1000';
          }
        }, 500);
      }
      
      // Get the icon component for the current step
      const getStepIcon = () => {
        // Use the emoji from the description if it starts with one
        const emojiMatch = currentStep.description.match(/^([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}])/u);
        if (emojiMatch) {
          return emojiMatch[1];
        }
        
        // Fallback to hash-based icons
        switch (currentStep.hash) {
          case '#home': return '🏠';
          case '#receipts': return '💷';
          case '#claims': return '📄';
          case '#reports': return '📊';
          case '#settings': return '⚙️';
          default: return '📍';
        }
      };
      
      // Add a small delay to ensure the previous toast is fully dismissed
      setTimeout(() => {
        const toastId = toast.info(`${getStepIcon()} ${currentStep.title}`, {
          description: currentStep.description,
          duration: tourStep === tourSteps.length - 1 ? 5000 : Infinity,
          onDismiss: () => {
            // If navigating to next step, don't reset the tour
            if (isNavigatingRef.current) {
              isNavigatingRef.current = false;
              return;
            }
            // Remove all highlights
            document.querySelectorAll('.tour-highlight').forEach(el => {
              el.classList.remove('tour-highlight');
              el.style.position = '';
              el.style.zIndex = '';
            });
            // Otherwise, reset tour completely when dismissed (X button clicked)
            isShowingToastRef.current = false;
            setIsTourActive(false);
            setTourStep(0);
            shouldStopTourRef.current = false;
          },
          onAutoClose: () => {
            // Remove all highlights
            document.querySelectorAll('.tour-highlight').forEach(el => {
              el.classList.remove('tour-highlight');
              el.style.position = '';
              el.style.zIndex = '';
            });
            // Always reset tour completely when auto-closed
            isShowingToastRef.current = false;
            setIsTourActive(false);
            setTourStep(0);
            shouldStopTourRef.current = false;
          },
          action: tourStep < tourSteps.length - 1 ? {
            label: `Next (${tourStep + 2}/${tourSteps.length})`,
            onClick: () => {
              // Set flag to prevent onDismiss from resetting the tour
              isNavigatingRef.current = true;
              toast.dismiss(toastIdRef.current);
              isShowingToastRef.current = false;
              setTourStep(tourStep + 1);
            },
          } : {
            label: 'Finish Tour',
            onClick: () => {
              // Remove all highlights
              document.querySelectorAll('.tour-highlight').forEach(el => {
                el.classList.remove('tour-highlight');
                el.style.position = '';
                el.style.zIndex = '';
              });
              shouldStopTourRef.current = true;
              isShowingToastRef.current = false;
              setIsTourActive(false);
              setTourStep(0);
              setIsHelpDialogOpen(false);
              toast.dismiss();
              // Navigate to home page after tour completion
              window.location.hash = 'home';
              toast.success('🎉 Tour completed!', {
                description: 'You\'re all set to start using Recap',
              });
            },
          },
          cancel: tourStep > 0 ? {
            label: 'Previous',
            onClick: () => {
              // Set flag to prevent onDismiss from resetting the tour
              isNavigatingRef.current = true;
              toast.dismiss(toastIdRef.current);
              isShowingToastRef.current = false;
              setTourStep(tourStep - 1);
            },
          } : {
            label: 'Skip Tour',
            onClick: () => {
              // Remove all highlights
              document.querySelectorAll('.tour-highlight').forEach(el => {
                el.classList.remove('tour-highlight');
                el.style.position = '';
                el.style.zIndex = '';
              });
              shouldStopTourRef.current = true;
              isShowingToastRef.current = false;
              setIsTourActive(false);
              setTourStep(0);
              setIsHelpDialogOpen(false);
              toast.dismiss();
            },
          },
        });
        
        // Store the toast ID for later dismissal
        toastIdRef.current = toastId;
      }, 100);
    }
  }, [isTourActive, tourStep, tourSteps]);

  const handleMenuItemClick = () => {
    // Close sidebar on mobile after clicking a menu item
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const startProductTour = () => {
    setIsHelpDialogOpen(false);
    setIsTourActive(true);
    setTourStep(0);
  };

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      // Tour completed
      setIsTourActive(false);
      setTourStep(0);
      setIsHelpDialogOpen(false);
      toast.success('Tour completed!', {
        description: "You're all set to start using Recap",
      });
    }
  };

  const skipTour = () => {
    setIsTourActive(false);
    setTourStep(0);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <RecapIcon className="h-10 w-10" />
          <div className="flex-1 group-data-[collapsible=icon]:hidden">
            <h2 className="text-lg font-bold">Recap</h2>
            <p className="text-xs text-muted-foreground">Snap it. Submit it. Sorted.</p>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={currentHash === item.url}>
                    <a href={item.url} onClick={handleMenuItemClick}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={currentHash === '#receipt-types'}>
                  <a href="#receipt-types" onClick={handleMenuItemClick}>
                    <Tags className="h-4 w-4" />
                    <span>Receipt Types</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={currentHash === '#integrations'}>
                  <a href="#integrations" onClick={handleMenuItemClick}>
                    <Plug className="h-4 w-4" />
                    <span>Integrations</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center gap-2">
            <div className="flex flex-col gap-1">
              <Dialog open={isHelpDialogOpen} onOpenChange={setIsHelpDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start group-data-[collapsible=icon]:justify-center"
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span className="group-data-[collapsible=icon]:hidden ml-2">Help</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Help & Support</DialogTitle>
                  <DialogDescription>
                    Learn how to use Recap to manage your expenses efficiently
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Key Features</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Home className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Home:</strong> View your dashboard with recent activity and quick stats
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <PoundSterling className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Receipts:</strong> Capture and manage all your expense receipts in one place
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Reports:</strong> Analyze your spending patterns and export data
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <Tags className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Receipt Types:</strong> Manage and customize your receipt categories
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <Plug className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Integrations:</strong> Configure integrations with expense management systems
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Tips & Tricks</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                      <li>Snap photos of receipts immediately to avoid losing them</li>
                      <li>Add notes and categories to receipts for better organization</li>
                      <li>Review and submit expense claims regularly</li>
                      <li>Use the reports section to track your spending trends</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Need More Help?</h3>
                    <p className="text-sm text-muted-foreground">
                      For additional support, please contact your system administrator or visit our documentation.
                    </p>
                  </div>
                </div>
              </DialogContent>
              </Dialog>
              <Button 
                onClick={startProductTour} 
                variant="ghost"
                size="sm"
                className="w-full justify-start group-data-[collapsible=icon]:justify-center h-8"
              >
                <Play className="h-3 w-3" />
                <span className="group-data-[collapsible=icon]:hidden ml-2 text-xs">Product tour</span>
              </Button>
            </div>
            <ThemeToggle />
          </div>
          <span className="text-xs text-muted-foreground group-data-[collapsible=icon]:hidden text-center">
            © 2024 Recap
          </span>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
